import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  Box,
  Button,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Modal from "@/components/Modal";
import UploadFileStep from "@/components/UploadFilesModal/UploadFileStep.tsx";
import SVGUploadFile from "@/assets/icons/SVGUploadFile.tsx";
import { StyledIconButton, StyledRightActionButton } from "@/components/styled";
import FileTypeForm from "@/components/UploadProjectFile/FileTypeForm.tsx";
import { ProjectRecord } from "@/types";
import { FILE_TYPES } from "@/constants";

export type UploadFileForm = {
  file: File;
  type: string;
  otherFileType?: string;
  serialNumber?: string;
  quarterlyPerformanceIncentiveValue?: string;
  performanceYear?: string;
  quarterName?: string;
};

const UploadProjectFile = ({
  uploadFile,
  isButton = false,
  showProjectSelect = false,
  showSerialNumberInput = true,
  showPerformanceReportInput = true,
  batteryOem,
}: {
  uploadFile: (data: FormData, id?: ProjectRecord["id"]) => Promise<void>;
  isButton?: boolean;
  showProjectSelect?: boolean;
  showSerialNumberInput?: boolean;
  showPerformanceReportInput?: boolean;
  batteryOem?: string | null;
}) => {
  const theme = useTheme();
  const downSm = useMediaQuery(theme.breakpoints.down("sm"));
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const {
    handleSubmit,
    reset,
    control,
    watch,
    formState: { isValid },
  } = useForm<UploadFileForm>({
    mode: "onChange",
  });
  const file = watch("file");
  const fileType = watch("type");
  const otherFileType = watch("otherFileType");
  const performanceYear = watch("performanceYear");

  const quarterlyPerformanceIncentiveValue = watch(
    "quarterlyPerformanceIncentiveValue"
  );

  useEffect(() => {
    if (step === 1 && open) {
      reset({ file });
    }
  }, [step, reset, file, open]);

  const handleClose = () => {
    setOpen(false);
    reset({ file: undefined });
    setStep(1);
    setLoading(false);
  };

  const nextStep = () => setStep((prev) => prev + 1);

  const prevStep = () => {
    setStep((prev) => prev - 1);
    setTimeout(() => {
      reset({ file });
    }, 10);
  };

  const onSubmit = async (data: any) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", data.file);

      switch (data.type) {
        case FILE_TYPES.SERIAL_NUMBER: {
          formData.append("type", data.type);
          if (showSerialNumberInput) {
            formData.append("serialNumber", data.serialNumber);
          }
          break;
        }
        case FILE_TYPES.PERFORMANCE_REPORT: {
          formData.append("type", data.type);
          if (showPerformanceReportInput) {
            formData.append(
              "quarterlyPerformanceIncentiveValue",
              data.quarterlyPerformanceIncentiveValue
            );

            formData.append("quarterName", data.quarterName);

            formData.append("performanceYear", data.performanceYear);
          }
          break;
        }
        case FILE_TYPES.OTHER: {
          formData.append("type", data.otherFileType || "");
          break;
        }
        default: {
          formData.append("type", data.type);
        }
      }

      if (data?.project?.id) {
        await uploadFile(formData, data?.project?.id);
      } else {
        await uploadFile(formData);
      }
      reset();
      handleClose();
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const isLastStep = step === 2;
  const isNextStepDisabled = () => {
    if (step === 1) return !file;
    if (step === 2) {
      switch (fileType) {
        case FILE_TYPES.PERFORMANCE_REPORT:
          return (
            !quarterlyPerformanceIncentiveValue && showPerformanceReportInput
          );
        case FILE_TYPES.OTHER:
          return !otherFileType;
        default:
          return !fileType;
      }
    }
    return false;
  };

  return (
    <>
      {isButton && (
        <Box
          sx={{
            ...(!downSm && { maxWidth: "180px", width: "100%" }),
          }}
        >
          <StyledRightActionButton
            onClick={() => setOpen(true)}
            sx={{ width: "100%", gap: "6px", whiteSpace: "nowrap" }}
            variant={"contained"}
          >
            <SVGUploadFile />
            {!downSm && "Upload file"}
          </StyledRightActionButton>
        </Box>
      )}
      {!isButton && (
        <StyledIconButton
          sx={{ flex: 1 }}
          variant={downSm ? "outlined" : "text"}
          onClick={() => setOpen(true)}
        >
          {downSm ? "Upload file" : <SVGUploadFile />}
        </StyledIconButton>
      )}
      <Modal open={open} onClose={handleClose} title={"Upload file"}>
        <form
          style={{
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
            marginTop: "24px",
          }}
          onSubmit={handleSubmit(onSubmit)}
        >
          {step === 1 && <UploadFileStep file={file} control={control} />}
          {step === 2 && (
            <FileTypeForm
              fileInfo={file}
              control={control}
              fileType={fileType}
              showProjectSelect={showProjectSelect}
              showSerialNumberInput={showSerialNumberInput}
              showPerformanceReportInput={showPerformanceReportInput}
              performanceYear={performanceYear}
              batteryOem={batteryOem}
            />
          )}
          <Box
            sx={{
              marginTop: "32px",
              display: "flex",
              gap: "8px",
              width: "100%",
            }}
          >
            <Button
              variant={"outlined"}
              sx={{ width: "100%" }}
              onClick={() => (step === 1 ? handleClose() : prevStep())}
            >
              Back
            </Button>
            {isLastStep && (
              <Button
                variant={"contained"}
                sx={{ width: "100%" }}
                type={"submit"}
                disabled={!isValid || isNextStepDisabled() || loading}
              >
                {loading ? (
                  <>
                    <CircularProgress
                      size={20}
                      sx={{ color: "#ffffff", mr: 1 }}
                    />
                    Uploading...
                  </>
                ) : (
                  "Upload file"
                )}
              </Button>
            )}
            {!isLastStep && (
              <Button
                variant={"contained"}
                sx={{ width: "100%" }}
                onClick={nextStep}
                disabled={!isValid || isNextStepDisabled()}
              >
                Continue
              </Button>
            )}
          </Box>
        </form>
      </Modal>
    </>
  );
};

export default UploadProjectFile;
